import { useNavigate } from "react-router-dom";
import { FiPlus, FiUser } from "react-icons/fi";
import { useState, useRef, useEffect } from "react";

const API_URL = process.env.REACT_APP_API_URL || "http://127.0.0.1:8000";

export default function Header({ onNewChat }) {
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const username = localStorage.getItem("username") || "User";

  const [subscriptionStatus, setSubscriptionStatus] = useState(null);
  const [accessExpiresAt, setAccessExpiresAt] = useState(null);

  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [changeMessage, setChangeMessage] = useState("");

  const handleLogout = () => {
    localStorage.removeItem("token");
    navigate("/login");
  };

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    fetch(`${API_URL}/me`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then((res) => res.json())
      .then((data) => {
        setSubscriptionStatus(data.subscription_status || "N/A");
        setAccessExpiresAt(data.access_expires_at || null);
      })
      .catch(() => {
        setSubscriptionStatus("N/A");
        setAccessExpiresAt(null);
      });
  }, []);

  const getTimeLeft = (isoDate) => {
    const expiry = new Date(isoDate);
    const now = new Date();
    const diff = expiry - now;

    if (diff <= 0) return "expired";

    const totalMinutes = Math.floor(diff / 1000 / 60);
    const days = Math.floor(totalMinutes / (60 * 24));
    const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
    const minutes = totalMinutes % 60;

    const parts = [];
    if (days > 0) parts.push(`${days} day${days !== 1 ? "s" : ""}`);
    if (hours > 0) parts.push(`${hours} hour${hours !== 1 ? "s" : ""}`);
    if (minutes > 0) parts.push(`${minutes} minute${minutes !== 1 ? "s" : ""}`);

    return parts.length > 0 ? parts.join(", ") : "less than a minute";
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleChangePassword = async () => {
    const res = await fetch("http://127.0.0.1:8000/change-password", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      body: JSON.stringify({
        old_password: oldPassword,
        new_password: newPassword,
      }),
    });

    const data = await res.json();
    if (res.ok) {
      setChangeMessage("Password changed successfully.");
      setTimeout(() => setShowPasswordModal(false), 1500);
    } else {
      setChangeMessage(data.detail || "Error changing password.");
    }
  };

    return (
    <>
      <header className="bg-white border-b border-gray-200 shadow-sm px-6 py-4 rounded-2xl mt-4 mx-4 flex justify-between items-center">
        <div className="flex items-center space-x-3">
          <img
            src="/logo.png"
            alt="DiagCode.AI"
            className="h-5 md:h-8 ml-6 md:ml-0"
          />
        </div>
        <div className="flex items-center gap-3 relative" ref={menuRef}>
          <button
            className="flex items-center justify-center gap-2 bg-[#140D4F] hover:bg-[#0f0a3c] text-white font-medium px-2 py-2 md:px-4 md:py-2 rounded-xl transition"
            onClick={onNewChat}
          >
            <FiPlus size={18} />
            <span className="hidden md:inline">New Chat</span>
          </button>
          <button
            className="bg-[#140D4F] hover:bg-[#0f0a3c] text-white p-2 rounded-full focus:outline-none"
            onClick={() => setMenuOpen((prev) => !prev)}
          >
            <FiUser size={20} />
          </button>
          {menuOpen && (
            <div className="absolute right-0 top-14 w-64 bg-white border border-gray-200 shadow-xl rounded-xl text-sm text-[#140D4F] z-50">
              <div className="px-4 py-2 border-b border-gray-200">
                <div className="text-xs text-gray-500">Signed in as:</div>
                <div className="font-semibold text-[#140D4F] truncate">{username}</div>
              </div>

              <div className="px-4 py-2 border-b border-gray-200">
                <div className="text-sm">
                  <span className="font-medium">Plan:</span>{" "}
                  <span className="text-[#0267C1]">{subscriptionStatus}</span>
                </div>
                {accessExpiresAt && (
                  <div className="text-xs text-gray-500">
                    expires in: {getTimeLeft(accessExpiresAt)}
                  </div>
                )}
              </div>

              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                onClick={() => navigate("/plans")}
              >
                Change Plan
              </button>
              <button
                className="w-full text-left px-4 py-2 hover:bg-gray-100 transition"
                onClick={() => setShowPasswordModal(true)}
              >
                Change Password
              </button>
              <button
                className="w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 border-t border-gray-200 transition"
                onClick={handleLogout}
              >
                Logout
              </button>
            </div>
          )}
        </div>
      </header>

      {showPasswordModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-96 text-black">
            <h2 className="text-xl font-semibold mb-4">Change Password</h2>
            <input
              type="password"
              placeholder="Current password"
              className="w-full p-2 border mb-2"
              value={oldPassword}
              onChange={(e) => setOldPassword(e.target.value)}
            />
            <input
              type="password"
              placeholder="New password"
              className="w-full p-2 border mb-4"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />
            {changeMessage && <div className="text-sm text-red-500 mb-2">{changeMessage}</div>}
            <div className="flex justify-end gap-2">
              <button
                className="px-4 py-2 bg-gray-300 rounded"
                onClick={() => setShowPasswordModal(false)}
              >
                Cancel
              </button>
              <button
                className="px-4 py-2 bg-blue-600 text-white rounded"
                onClick={handleChangePassword}
              >
                Change
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
